import React from 'react';
import singleTenantTables from './single-tenant-tables/index';
import Textarea from '../Textarea';
import markets from '../../markets';
import propertyCategories from '../../property-categories';
import propertyTypes from '../../property-types';
import leaseTypes from '../../lease-types';
import unitedStates from 'united-states';
import request from "arcdynamic-request";
import {stringProps, dateProps, numberProps, vacantChanges} from "./stnl-fields-to-save";
import Icon from "../Icon";
import svgPlace from "../../svgs/place.svg";
import {stringUStoISO, stringISOtoUS, stringUStoUS} from '../../date-converter';
import guarantor from '../../guarantor';
import optionsToPurchase from '../../options-to-purchase';
import PrintSingleTenant from "../PrintSingleTenant";
import {addCommasToNumber} from "../../utils";
import {UNDERWRITING_SINGLE_TENANT, UNDERWRITING_RENT_SCHEDULE} from '../../consts/tables';
import {buildRentScheduleRow} from "./single-tenant-tables/rent-schedule";
import {buildOperatingDataRow} from "./single-tenant-tables/annualized-operating-data";
import plusIcon from "../../svgs/plus-button.svg";
import minusIcon from "../../svgs/minus-button.svg";

const cloneDeep = require('lodash.clonedeep');
const findIndex = require('lodash.findindex');
export const operatingDataNo = 11;
const monthlyRentIndex = 0;
const currentYearsIndex = 0;
const leaseCommencementIndex = 1;
const increaseIndex = 3;

export default class UnderwritingSingleTenant extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            data: {
                tables: singleTenantTables
            },
            original: {
                tables: singleTenantTables
            },
            rentScheduleData: null,
            newData: null,
            underwritingId: null,
            displayPopupSubmit: false,
            isAdditionalPriceVisible: false,
        };

        this.getUnderwritingData = this.getUnderwritingData.bind(this);
        this.setUnderwritingData = this.setUnderwritingData.bind(this);
        this.onTableValueChange = this.onTableValueChange.bind(this);
        this.onTableValueBlur = this.onTableValueBlur.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.togglePopupMessage = this.togglePopupMessage.bind(this);
        this.addRow = this.addRow.bind(this);
        this.deleteRow = this.deleteRow.bind(this);
        this.handleExpand = this.handleExpand.bind(this);
    }

    static calculateMonthlyRents(tables) {
        const t = cloneDeep(tables);
        const rentScheduleRows = t['rent-schedule'].rows;
        const operatingDataRows = t['annualized-operating-data'].rows;
        Object.keys(rentScheduleRows).forEach(key => {
            if (['0', '1'].includes(key) || !operatingDataRows[key].valueCells[0].value) return;
            const prevIncrease = operatingDataRows[key - 1].valueCells[increaseIndex].getComputedValue(t, key - 1).internalValue;
            if (!prevIncrease) return;
            const prevMonthlyRent = rentScheduleRows[key - 1].valueCells[monthlyRentIndex].value;
            rentScheduleRows[key].valueCells[monthlyRentIndex].value = (prevMonthlyRent * (prevIncrease / 100 + 1)).toFixed(2);
        });

        return t;
    }

    static calculateLeaseCommencement(tables) {
        const t = cloneDeep(tables);
        const operatingDataRows = t['annualized-operating-data'].rows;

        Object.keys(operatingDataRows).forEach(key => {
            const leaseCommencement = t['lease-summary'].rows['lease-commencement'].value || '';
            const leaseExpiration = t['lease-summary'].rows['lease-expiration'].value || '';
            const currentYears = t['annualized-operating-data'].rows[key].valueCells[currentYearsIndex].value;

            if (key === '0' ) {
                if(currentYears)
                    operatingDataRows[key].valueCells[leaseCommencementIndex].value = leaseCommencement;
                return;
            }

            const leaseCommencementDate = new Date(leaseCommencement);
            const leaseExpirationDate = new Date(leaseExpiration);
            const previousYears = t['annualized-operating-data'].rows[key - 1].valueCells[currentYearsIndex].value;

            if (currentYears && previousYears && leaseCommencement) {
                const year = previousYears && previousYears.includes('-') ? parseInt(previousYears.split('-')[1]) : parseInt(previousYears);
                const date = {
                    year: leaseCommencementDate.getFullYear() + year,
                    month: leaseExpirationDate.getMonth() + 1,
                    day: leaseExpirationDate.getDate()
                };

                // If the previous commencement date is not the first of the month, the current date will adjust to the next month
                const currentDate = new Date(date.year, date.month,0).getDate();
                if(date.day === currentDate){
                    if (date.month === 12) {
                        date.month = 1;
                        date.year++;
                    } else {
                        date.month++;
                    }
                    date.day = 1;
                } else{
                    date.day ++;
                }
                operatingDataRows[key].valueCells[leaseCommencementIndex].value = year && date.month && date.day && date.year ? stringUStoUS(`${date.month}/${date.day}/${date.year}`) : '';
            }
        });

        return t;
    }

    componentDidMount() {
        this.getUnderwritingData();
    }

    getUnderwritingData(isSaved) {
        let data = cloneDeep(this.state.data);

        // Set the dropdown values
        data.tables['general-information'].rows['state'].dropdownValues = unitedStates;
        data.tables['general-information'].rows['market'].dropdownValues = markets;
        data.tables['general-information'].rows['property-category'].dropdownValues = propertyCategories;
        data.tables['general-information'].rows['property-type'].dropdownValues = propertyTypes;
        data.tables['lease-summary'].rows['lease-type'].dropdownValues = leaseTypes;
        data.tables['lease-summary'].rows['guarantor'].dropdownValues = guarantor;
        data.tables['lease-summary'].rows['options-to-purchase'].dropdownValues = optionsToPurchase;

        if (!this.props.data.ARCIMEDES_ROW_ID) {
            this.setState({
                data: data,
                original: cloneDeep(data)
            });
            return;
        }

        Promise.all([
            request(arc.path.api, {
                service: 'arcimedes',
                action: 'open.datasource.table.Data.getData',
                params: ['code', UNDERWRITING_SINGLE_TENANT, {
                    filter: [{
                        'text': this.props.data.ARCIMEDES_ROW_ID,
                        'code': 'PROPERTY_ID',
                        'type': 'exact',
                    }],
                }],
            }),
            request(arc.path.api, {
                service: 'arcimedes',
                action: 'open.datasource.table.Data.getData',
                params: ['code', UNDERWRITING_RENT_SCHEDULE, {
                    filter: [{
                        'text': this.props.data.ARCIMEDES_ROW_ID,
                        'code': 'PROPERTY_ID',
                        'type': 'exact',
                    }, {
                        'text': '',
                        'code': 'DELETED',
                        'type': 'null',
                    }],
                    filterType: 'and',
                    order: [{
                        code: 'ARCIMEDES_ROW_ID',
                        type: 'asc',
                    }]
                }],
            }),
        ]).then(res => {
            if (res)
                this.setUnderwritingData(res, data, isSaved);
        });
    }

    setUnderwritingData(res, data, isSaved) {
        const propsData = this.props.data;
        const annualizedOperatingData = data.tables['annualized-operating-data'];
        const rentSchedule = data.tables['rent-schedule'];
        let rentScheduleData = null;

        // Manage data from PROPERTIES table
        stringProps.map(prop => {
            // get property props
            if (propsData.hasOwnProperty(prop.key)) {
                data.tables[prop.table].rows[prop.tableKey].value = propsData[prop.key] || '';
            }
        });

        dateProps.map(prop => {
            // get property props
            if (propsData.hasOwnProperty(prop.key)) {
                data.tables[prop.table].rows[prop.tableKey].value = propsData[prop.key] ? stringUStoUS(propsData[prop.key]) : '';
            }
        });

        numberProps.map(prop => {
            // get property props
            if (propsData.hasOwnProperty(prop.key)) {
                if (prop.withReplace && prop.withToFixed) {
                    data.tables[prop.table].rows[prop.tableKey].value = propsData[prop.key] ? parseFloat(propsData[prop.key].replace(/,/g, '')).toFixed(2) : '';
                    return;
                }
                if (prop.withToFixed) {
                    data.tables[prop.table].rows[prop.tableKey].value = propsData[prop.key] ? parseFloat(propsData[prop.key]).toFixed(2) : '';
                    return;
                }
                if (prop.withReplace) {
                    data.tables[prop.table].rows[prop.tableKey].value = propsData[prop.key] ? parseFloat(propsData[prop.key].replace(/,/g, '')) : '';
                    return;
                }
                data.tables[prop.table].rows[prop.tableKey].value = parseFloat(propsData[prop.key]) || '';
            }
        });

        // Manage data from UNDERWRITING_SINGLE_TENANT table
        if (res[0] && res[0].success && Array.isArray(res[0].data)) {
            const result = res[0].data[0] ? res[0].data[0] : {};

            stringProps.map(prop => {
                // get underwriting data
                if (!propsData.hasOwnProperty(prop.key)) {
                    data.tables[prop.table].rows[prop.tableKey].value = result[prop.key] || '';
                }
            });

            dateProps.map(prop => {
                // get underwriting data
                if (!propsData.hasOwnProperty(prop.key)) {
                    data.tables[prop.table].rows[prop.tableKey].value = result[prop.key] ? stringISOtoUS(result[prop.key]) : '';
                }
            });

            numberProps.map(prop => {
                // get underwriting data
                if (!propsData.hasOwnProperty(prop.key)) {
                    if (prop.hasOwnProperty('valueCellIndex')) {
                        const defaultValue = prop.defaultValue || '';
                        if (prop.withToFixed) {
                            data.tables[prop.table].rows[prop.tableKey].valueCells[prop.valueCellIndex].value = result[prop.key] ? parseFloat(result[prop.key]).toFixed(2) : defaultValue;
                            return;
                        }
                        data.tables[prop.table].rows[prop.tableKey].valueCells[prop.valueCellIndex].value = parseFloat(result[prop.key]) || defaultValue;
                    } else {
                        data.tables[prop.table].rows[prop.tableKey].value = parseFloat(result[prop.key]) || '';
                    }
                }
            });

            data.tables['annualized-operating-data'].footerRow[1].valueCells[0].value = result.ADDITION_TYPE || '';
            data.tables['annualized-operating-data'].footerRow[1].valueCells[2].value = parseInt(result.ADDITION_VALUE) || '';
        }

        // Manage data from UNDERWRITING_RENT_SCHEDULE table
        if (res[1] && res[1].success && Array.isArray(res[1].data)) {
            const result = res[1].data;
            rentScheduleData = result;
            const no = result.length > operatingDataNo ? result.length : operatingDataNo;

            rentSchedule.rows = Object.assign({}, new Array(no).fill(null).map((row, index) => {
                const rowData = result[index] ? result[index] : {};
                return buildRentScheduleRow(rowData);
            }));

            annualizedOperatingData.rows = Object.assign({}, new Array(no).fill(null).map((row, index) => {
                const rowData = result[index] ? result[index] : {};
                return buildOperatingDataRow(rowData);
            }));

            if (result && result[0] && !result[0].LEASE_COMMENCEMENT)
                data.tables = UnderwritingSingleTenant.calculateLeaseCommencement(data.tables);
        }

        const leaseType = data.tables['lease-summary'].rows['lease-type'].value;
        data.tables = UnderwritingSingleTenant.changeVacantView(data.tables, leaseType);

        this.setState({
            data: data,
            original: cloneDeep(data),
            rentScheduleData,
            underwritingId: res[0].data[0] ? res[0].data[0].ARCIMEDES_ROW_ID : null
        }, () => {
            if (isSaved) this.props.parentSetData({success: 'Saved'});
        });
    }

    setFieldsDisabled(data) {
        const newData = cloneDeep(data);

        stringProps.map(prop => {
            newData.tables[prop.table].rows[prop.tableKey].forceStatic = !prop.hasOwnProperty('forceStatic');
        });
        dateProps.map(prop => {
            newData.tables[prop.table].rows[prop.tableKey].forceStatic = !prop.hasOwnProperty('forceStatic');
        });
        numberProps.map(prop => {
            if (prop.hasOwnProperty('valueCellIndex')) {
                newData.tables[prop.table].rows[prop.tableKey].valueCells[prop.valueCellIndex].forceStatic = !prop.hasOwnProperty('forceStatic');
            } else {
                newData.tables[prop.table].rows[prop.tableKey].forceStatic = !prop.hasOwnProperty('forceStatic');
            }
        });

        Object.keys(newData.tables['rent-schedule'].rows).map(key => {
            newData.tables['rent-schedule'].rows[key].valueCells[0].forceStatic = true;
        });

        Object.keys(newData.tables['annualized-operating-data'].rows).map(key => {
            newData.tables['annualized-operating-data'].rows[key].valueCells[0].forceStatic = true;
        });

        return newData;
    }

    togglePopupMessage() {
        this.setState({displayPopupSubmit: !this.state.displayPopupSubmit})
    }

    handleExpand(tableIdentifier) {
        const data = cloneDeep(this.state.data);
        data.tables[tableIdentifier].footerRow[1].valueCells.forEach(cell => {
            cell.hidden = !cell.hidden;
        });

        this.setState({
            data,
            isAdditionalPriceVisible: !this.state.isAdditionalPriceVisible,
        });
    }

    static changeVacantView(tables, leaseType) {
        const isVacantLeaseType = leaseType === 'Vacant';
        vacantChanges.map(prop => {
            if (prop.hasValueCells) {
                tables[prop.table].rows[prop.rowKey].valueCells.map(cell => {
                    cell[prop.property] = prop.getValue(isVacantLeaseType);
                });
            } else {
                tables[prop.table].rows[prop.rowKey][prop.property] = prop.getValue(isVacantLeaseType);
            }
        });
        return tables;
    }

    onTableValueChange(tableIdentifier, rowCode, event, columnIndex, isFooter) {
        const data = cloneDeep(this.state.data);
        const value = event.currentTarget.value;

        if (isFooter) {
            data.tables[tableIdentifier].footerRow[rowCode].valueCells[columnIndex].value = value;
        } else if (columnIndex === undefined || columnIndex === null) {
            if (tableIdentifier === 'lease-summary' && rowCode === 'lease-type' &&
                (data.tables[tableIdentifier].rows[rowCode].value !== 'Vacant' && value === 'Vacant' ||
                    data.tables[tableIdentifier].rows[rowCode].value === 'Vacant' && value !== 'Vacant')) {
                data.tables = UnderwritingSingleTenant.changeVacantView(data.tables, value);
            }
            data.tables[tableIdentifier].rows[rowCode].value = value;
        } else {
            data.tables[tableIdentifier].rows[rowCode].valueCells[columnIndex].value = value;
        }
        this.setState({data: data});
    }

    onTableValueBlur(tableIdentifier, rowCode, event, columnIndex) {
        const data = cloneDeep(this.state.data);
        const value = event.currentTarget.value;

        if(tableIdentifier === 'annualized-operating-data' && columnIndex === currentYearsIndex){
            data.tables = UnderwritingSingleTenant.calculateMonthlyRents(data.tables);
            data.tables = UnderwritingSingleTenant.calculateLeaseCommencement(data.tables);
            this.setState({data: data});
            return;
        }

        if (tableIdentifier === 'lease-summary' && rowCode === 'lease-commencement' ||
            tableIdentifier === 'lease-summary' && rowCode === 'lease-expiration') {
            data.tables = UnderwritingSingleTenant.calculateLeaseCommencement(data.tables);
            this.setState({data: data});
            return;
        }

        if (columnIndex === undefined || columnIndex === null)
            return;

        if (tableIdentifier === 'rent-schedule') {
            const val = value && !(/^[a-zA-Z]+$/g).test(value) ? parseFloat(value).toFixed(2) : value;
            data.tables[tableIdentifier].rows[rowCode].valueCells[columnIndex].value = val;
            // if the first 2 monthly rents exist, calculate the others
            if (columnIndex === monthlyRentIndex && val &&
                (rowCode === '0' && data.tables[tableIdentifier].rows[1].valueCells[columnIndex].value
                    || rowCode === '1' && data.tables[tableIdentifier].rows[0].valueCells[columnIndex].value))
                data.tables = UnderwritingSingleTenant.calculateMonthlyRents(data.tables);
        } else {
            const propIndex = findIndex(numberProps, {tableKey: rowCode});
            if (propIndex >= 0 && numberProps[propIndex].hasOwnProperty('withToFixed'))
                data.tables[tableIdentifier].rows[rowCode].valueCells[columnIndex].value = value ? parseFloat(value).toFixed(2) : '';
        }
        this.setState({data: data});
    }

    addRow() {
        const data = cloneDeep(this.state.data);
        const newRowIndex = Object.keys(data.tables['annualized-operating-data'].rows).length;
        data.tables['annualized-operating-data'].rows[newRowIndex] = buildOperatingDataRow({});
        data.tables['rent-schedule'].rows[newRowIndex] = buildRentScheduleRow({});
        this.setState({data});
    }

    deleteRow(rowIdentifier) {
        const data = cloneDeep(this.state.data);
        ['annualized-operating-data', 'rent-schedule'].map(tableId => {
            const rowsCount = Object.keys(data.tables[tableId].rows).length;
            if (rowsCount <= 1) {
                alert("You can't delete all the rows from the table!");
            } else {
                // move rows up and delete the last one
                for (let index = parseInt(rowIdentifier); index < rowsCount - 1; index++)
                    data.tables[tableId].rows[index] = data.tables[tableId].rows[index + 1];
                delete data.tables[tableId].rows[rowsCount - 1];
            }
        });
        this.setState({data});
    }

    static renderNameCell(row) {
        if (row.hidden) return;

        return (
            <td className='name-cell'>
                <span className='text'>{row.name}</span>
            </td>
        );
    }

    static renderStaticValueCell(tables, row, rowKey, domKey) {
        if (row.hidden) return;

        const displayValue = row.getComputedValue(tables, rowKey).displayValue;
        return (
            <td className={`${(displayValue === '-' ? 'center-dashes' : '')} value-cell ${(row.styleClasses && row.styleClasses.length ? row.styleClasses : '')}`}
                key={domKey}>
                <span className='text value'>{displayValue}</span>
                {row.tooltip ? <span className='cell-tooltip'>{row.tooltip}</span> : null}
            </td>
        );
    }

    renderEditableValueCell(tableIdentifier, rowKey, row, columnIndex, hasRowManipulation, isFooter) {
        if (row.hidden) return;

        const valueNeedsFormatting = row.valueNeedsFormatting || (row.type === 'number' && (row.unit === '$' || row.unit === '%' || row.unit === 'SF' || row.unit === 'Acres' || row.unit === 'Years' || row.unit === 'bps'));
        if (row.forceStatic) {
            return (
                <td className={`value-cell ${(row.styleClasses && row.styleClasses.length ? row.styleClasses : '')}`}
                    key={`${rowKey}-${columnIndex}`}>
                    {tableIdentifier === 'general-information' && rowKey === 'google-maps-link' && row.value ?
                        <a className='link' href={row.value} target='_blank'>{row.value}</a>
                        :
                        <span className='text value'>{row.value.toLocaleString()}</span>
                    }
                </td>
            )
        }

        return (
            <td className='value-cell editable-cell' key={`${rowKey}-${columnIndex}`}>
                {row.type === 'dropdown' ?
                    row.name === 'State' ?
                        <label>
                            <select
                                onChange={(event) => this.onTableValueChange(tableIdentifier, rowKey, event, columnIndex)}
                                value={row.value}>
                                <option value=''/>
                                {row.dropdownValues ? row.dropdownValues.filter(el => !el.isTerritory).map(elem =>
                                    <option key={elem.hasOwnProperty('abbr') ? elem.abbr : elem} value={elem.hasOwnProperty('abbr') ? elem.abbr : elem}>{elem.hasOwnProperty('name') ? elem.name : elem}</option>) : null}
                            </select>
                        </label>
                        :
                        <label>
                            <select
                                onChange={(event) => this.onTableValueChange(tableIdentifier, rowKey, event, columnIndex)}
                                value={row.value}>
                                <option value=''/>
                                {row.dropdownValues ? row.dropdownValues.map(elem =>
                                    <option key={elem.hasOwnProperty('code') ? elem.code : elem} value={elem.hasOwnProperty('code') ? elem.code : elem}>{elem.hasOwnProperty('name') ? elem.name : elem}</option>) : null}
                            </select>
                        </label>
                    :
                    hasRowManipulation ?
                        <div>
                            <label className={`${valueNeedsFormatting ? 'formatted-number-label' : ''} ${row.type}`}>
                                <input value={row.value}
                                       onChange={(event) => this.onTableValueChange(tableIdentifier, rowKey, event, columnIndex)}
                                       onBlur={(event) => this.onTableValueBlur(tableIdentifier, rowKey, event, columnIndex)}
                                       type={row.type !== 'date' ? row.type : null}
                                       placeholder={row.withoutPlaceholder ? '' : (row.type === 'date' ? 'MM/DD/YYYY' : row.unit)}/>
                                <div className="input-display-value">
                                    {row.value && !(/^[a-zA-Z]+$/g).test(row.value) && row.unit === '$' ? row.unit : null}
                                    {row.value ?
                                        row.withDecimals ? Number(row.value).toFixed(2) : addCommasToNumber(row.value)
                                        : null}
                                    {row.value && row.unit !== '$' ? (row.unit === 'Acres' || row.unit === 'SF' ? ` ${row.unit}` : row.unit) : null}
                                </div>
                            </label>
                            <div className="more-button">
                                <div className="dot"/>
                                <div className="dot"/>
                                <div className="dot"/>
                                <div className="row-manipulation-container">
                                    <div className="action-button" onClick={() => this.deleteRow(rowKey)}>Delete row</div>
                                    <div className="action-button" onClick={this.addRow}>Add row</div>
                                </div>
                            </div>
                        </div>
                        :
                        <label className={`${valueNeedsFormatting ? 'formatted-number-label' : ''} ${row.type}`}>
                            <input value={row.value}
                                   onChange={(event) => this.onTableValueChange(tableIdentifier, rowKey, event, columnIndex, isFooter)}
                                   onBlur={(event) => this.onTableValueBlur(tableIdentifier, rowKey, event, columnIndex)}
                                   type={row.type !== 'date' ? row.type : null}
                                   placeholder={row.withoutPlaceholder ? '' : (row.type === 'date' ? 'MM/DD/YYYY' : row.unit)}/>
                            <div className="input-display-value">
                                {row.value && !(/^[a-zA-Z]+$/g).test(row.value) && row.unit === '$' ? row.unit : null}
                                {row.value ?
                                    row.withDecimals ? Number(row.value).toFixed(2) : addCommasToNumber(row.value)
                                    : null}
                                {row.value && row.unit !== '$' ? (row.unit === 'Acres' || row.unit === 'SF' ? ` ${row.unit}` : row.unit) : null}
                            </div>
                            {row.name === 'Address' ?
                                <a className="link-button"
                                   href={arc.path.base + 'map/' + this.props.data.ARCIMEDES_ROW_ID} target="_blank">
                                    <Icon svg={svgPlace}/>
                                </a>
                                :
                                <a className="link-button" href={row.value} target="_blank">
                                    <svg aria-hidden="true" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512">
                                        <path fill="currentColor" d="M576 24v127.984c0 21.461-25.96 31.98-40.971 16.971l-35.707-35.709-243.523 243.523c-9.373 9.373-24.568 9.373-33.941 0l-22.627-22.627c-9.373-9.373-9.373-24.569 0-33.941L442.756 76.676l-35.703-35.705C391.982 25.9 402.656 0 424.024 0H552c13.255 0 24 10.745 24 24zM407.029 270.794l-16 16A23.999 23.999 0 0 0 384 303.765V448H64V128h264a24.003 24.003 0 0 0 16.97-7.029l16-16C376.089 89.851 365.381 64 344 64H48C21.49 64 0 85.49 0 112v352c0 26.51 21.49 48 48 48h352c26.51 0 48-21.49 48-48V287.764c0-21.382-25.852-32.09-40.971-16.97z"/>
                                    </svg>
                                </a>
                            }
                        </label>
                }
            </td>
        );
    }

    renderTable(tables, tableIdentifier) {
        const table = tables[tableIdentifier];

        const tableRows = Object.keys(table.rows).map(key => {
            const row = table.rows[key];
            const nameCell = UnderwritingSingleTenant.renderNameCell(row);
            let valueCell;

            if (row.editable) {
                valueCell = this.renderEditableValueCell(tableIdentifier, key, row);
            } else {
                valueCell = UnderwritingSingleTenant.renderStaticValueCell(tables, row, key);
            }

            return (
                <tr key={key} className={key}>{nameCell}{valueCell}</tr>
            );
        });

        return (
            <table className={`Financials_table ${tableIdentifier}-table`} key={tableIdentifier}>
                <tbody>{tableRows}</tbody>
            </table>
        )
    }

    renderMatrixTable(tableIdentifier) {
        const tables = this.state.data.tables;
        const table = tables[tableIdentifier];

        const tableRows = Object.keys(table.rows).map((key) => {
            const row = table.rows[key];
            if (row.hidden) return;

            const nameCell = UnderwritingSingleTenant.renderNameCell(row);

            return <tr key={key} className={key}>
                {table.hasNamesColumn ? nameCell : null}

                {row.valueCells.map((cell, index) => {
                    if (cell === null) {
                        return <td key={index} className='invisible'/>
                    }
                    if (cell.editable) {
                        const hasRowManipulation = cell.hasRowManipulation;
                        return this.renderEditableValueCell(tableIdentifier, key, cell, index, hasRowManipulation);
                    } else {
                        const domKey = `${key}-${index}`;
                        return UnderwritingSingleTenant.renderStaticValueCell(tables, cell, key, domKey);
                    }
                })}
            </tr>
        });

        return (
            <table className={`Financials_table ${tableIdentifier}-table`} key={tableIdentifier}>
                <tbody>
                {table.headerRow && table.headerRow.length ?
                    <tr>{table.headerRow.map((cell) => {
                        return (
                            <td className='header-cell' key={cell}>
                                <div className='text'>{cell}</div>
                            </td>
                        )
                    })}</tr>
                    : null
                }

                {tableRows}

                {table.footerRow && Array.isArray(table.footerRow) ?
                    table.footerRow.map((row, index) => {
                        return (
                            <tr className='footer-row' key={index}>{Object.keys(row.valueCells).map((key) => {
                                const cell = row.valueCells[key];
                                if (cell && cell.hidden)
                                    return;

                                if (cell && !cell.empty) {
                                    if (cell.hasExpandButton)
                                        return (
                                            <td className='invisible' key={key}>
                                                <div className='expand-button' onClick={() => this.handleExpand(tableIdentifier)}>
                                                    <Icon svg={this.state.isAdditionalPriceVisible ? minusIcon : plusIcon}/>
                                                </div>
                                            </td>
                                        );

                                    if (cell.editable)
                                        return this.renderEditableValueCell(tableIdentifier, index, cell, key, null, true);

                                    const cellComputedValue = cell.getComputedValue(tables);
                                    return (
                                        <td key={key}>
                                            <div className={`text${cell.hasExpandButton ? ' with-expand' : ''}`}>
                                                {cellComputedValue.hasOwnProperty('displayValue') ? cellComputedValue.displayValue : cellComputedValue}
                                                {
                                                    cell.hasExpandButton &&
                                                    <div className='expand-button' onClick={() => this.handleExpand(tableIdentifier)}>
                                                        <Icon svg={plusIcon}/>
                                                    </div>
                                                }
                                            </div>
                                        </td>
                                    );
                                }
                                return <td className='invisible' key={key}/>;
                            })}</tr>
                        )
                    })
                    :
                    table.footerRow && table.footerRow.valueCells && table.footerRow.valueCells.length ?
                        <tr className='footer-row'>{Object.keys(table.footerRow.valueCells).map((key) => {
                            const cell = table.footerRow.valueCells[key];
                            if (cell) {
                                const cellComputedValue = cell.getComputedValue(tables);
                                return (
                                    <td key={key}>
                                        <div className='text'>
                                            {cellComputedValue.hasOwnProperty('displayValue') ? cellComputedValue.displayValue : cellComputedValue}
                                        </div>
                                    </td>
                                );
                            }
                            return <td className='invisible' key={key}/>;
                        })}</tr>
                        : null
                }
                </tbody>
            </table>
        )
    }

    static renderTableContainer(tables, tableIdentifier, tablesToDisplay) {
        const table = tables[tableIdentifier];
        return (
            <div className={`Financials_table_container ${tableIdentifier}-table-container`}>
                <h3 className='Financials_table_title'>{table.name}
                    <span className='Financials_table_description'>{table.description}</span>
                </h3>

                {tablesToDisplay.map(table => table)}
            </div>
        )
    }

    onSubmit() {
        const tables = this.state.data.tables;
        const originalTables = this.state.original.tables;
        const rentScheduleData = this.state.rentScheduleData;
        const propsData = cloneDeep(this.props.data);
        const propertyId = this.props.data.ARCIMEDES_ROW_ID;
        const underwritingData = {};

        stringProps.map(prop => {
            if (prop.hasOwnProperty('fromProps')) {
                // set property props
                propsData[prop.key] = tables[prop.table].rows[prop.tableKey].value;
            } else {
                // set underwritingData just if the value has been changed
                if (tables[prop.table].rows[prop.tableKey].value !== originalTables[prop.table].rows[prop.tableKey].value) {
                    underwritingData[prop.key] = tables[prop.table].rows[prop.tableKey].value;
                }
            }
        });

        dateProps.map(prop => {
            if (prop.hasOwnProperty('fromProps')) {
                // set property props
                propsData[prop.key] = tables[prop.table].rows[prop.tableKey].value;
            } else {
                // set underwritingData just if the value has been changed
                if (tables[prop.table].rows[prop.tableKey].value !== originalTables[prop.table].rows[prop.tableKey].value) {
                    underwritingData[prop.key] = tables[prop.table].rows[prop.tableKey].value ? stringUStoISO(tables[prop.table].rows[prop.tableKey].value) : '';
                }
            }
        });

        numberProps.map(prop => {
            if (prop.hasOwnProperty('fromProps')) {
                // set property props
                if (prop.withReplace) {
                    propsData[prop.key] = addCommasToNumber(tables[prop.table].rows[prop.tableKey].value);
                    return;
                }
                propsData[prop.key] = tables[prop.table].rows[prop.tableKey].value.toString();
            } else {
                // set underwritingData just if the value has been changed
                if (prop.hasOwnProperty('valueCellIndex')) {
                    const defaultValue = prop.defaultValue || '';
                    if (tables[prop.table].rows[prop.tableKey].valueCells[prop.valueCellIndex].value !== originalTables[prop.table].rows[prop.tableKey].valueCells[prop.valueCellIndex].value ||
                        prop.hasOwnProperty('defaultValue') && !tables[prop.table].rows[prop.tableKey].valueCells[prop.valueCellIndex].value) {
                        underwritingData[prop.key] = parseFloat(tables[prop.table].rows[prop.tableKey].valueCells[prop.valueCellIndex].value) || defaultValue;
                    }
                } else {
                    if (tables[prop.table].rows[prop.tableKey].value !== originalTables[prop.table].rows[prop.tableKey].value) {
                        underwritingData[prop.key] = parseFloat(tables[prop.table].rows[prop.tableKey].value) || '';
                    }
                }
            }
        });

        if (tables['annualized-operating-data'].footerRow[1].valueCells[0].value !== originalTables['annualized-operating-data'].footerRow[1].valueCells[0].value)
            underwritingData.ADDITION_TYPE = tables['annualized-operating-data'].footerRow[1].valueCells[0].value;
        if (tables['annualized-operating-data'].footerRow[1].valueCells[2].value !== originalTables['annualized-operating-data'].footerRow[1].valueCells[2].value)
            underwritingData.ADDITION_VALUE = tables['annualized-operating-data'].footerRow[1].valueCells[2].value;

        const noi = tables['offering-summary'].rows['net-operating-income'].getComputedValue(tables).internalValue;
        if (noi) {
            propsData.NET_OPERATING_INCOME = addCommasToNumber(noi);
            propsData.BASE_RENT = propsData.NET_OPERATING_INCOME;
        }

        const price = tables['offering-summary'].rows['price'].getComputedValue(tables).internalValue;
        if (price) {
            propsData.LIST_PRICE = addCommasToNumber(price);
        }

        const rentSchedule = tables['rent-schedule'].rows;
        const annualizedOperatingData = tables['annualized-operating-data'].rows;
        const newRentScheduleData = [];
        const deletedRows = rentScheduleData ? cloneDeep(rentScheduleData) : [];
        const arcRowIdIndex = annualizedOperatingData[0].valueCells.length - 1;

        Object.keys(rentSchedule).forEach(key => {
            const rentObj = {};
            const rowId = annualizedOperatingData[key].valueCells[arcRowIdIndex].value;

            // remove existing rows in order to have only deleted ones
            if (rowId) {
                const index = findIndex(deletedRows, {ARCIMEDES_ROW_ID: rowId});
                if (index > -1)
                    deletedRows.splice(index, 1);
            }

            if ((originalTables['rent-schedule'].rows[key] &&
                rentSchedule[key].valueCells[monthlyRentIndex].value !== originalTables['rent-schedule'].rows[key].valueCells[monthlyRentIndex].value) ||
                !originalTables['rent-schedule'].rows[key]) {
                rentObj.MONTHLY_RENT = rentSchedule[key].valueCells[monthlyRentIndex].value;
            }
            if ((originalTables['annualized-operating-data'].rows[key] &&
                annualizedOperatingData[key].valueCells[currentYearsIndex].value !== originalTables['annualized-operating-data'].rows[key].valueCells[currentYearsIndex].value) ||
                !originalTables['annualized-operating-data'].rows[key]) {
                rentObj.YEARS = annualizedOperatingData[key].valueCells[currentYearsIndex].value;
            }
            if ((originalTables['annualized-operating-data'].rows[key] &&
                annualizedOperatingData[key].valueCells[leaseCommencementIndex].value !== originalTables['annualized-operating-data'].rows[key].valueCells[leaseCommencementIndex].value) ||
                !originalTables['annualized-operating-data'].rows[key]) {
                rentObj.LEASE_COMMENCEMENT = annualizedOperatingData[key].valueCells[leaseCommencementIndex].value ? stringUStoISO(annualizedOperatingData[key].valueCells[leaseCommencementIndex].value) : '';
            }

            // Set only if there was at least a change and the property exists
            if (Object.keys(rentObj).length && propertyId) {
                rentObj.PROPERTY_ID = propertyId;
                if (rowId)
                    rentObj.ARCIMEDES_ROW_ID = rowId;
                newRentScheduleData.push(rentObj);
            }
        });

        deletedRows.map(row => {
            newRentScheduleData.push({
                ARCIMEDES_ROW_ID: row.ARCIMEDES_ROW_ID,
                DELETED: 1,
            });
        });

        if (!propertyId)
            propsData.ARCIMEDES_ROW_ID = this.props.newPropertyId;

        // Sent back the property props
        this.setState({newData: propsData});
        this.props.parentSetData({newData: propsData});

        const noChangesDetected = !Object.keys(underwritingData).length && newRentScheduleData.length < 1;
        if (Object.keys(underwritingData).length)
            underwritingData.PROPERTY_ID = propertyId ? propertyId : this.props.newPropertyId;

        // No changes detected for Underwriting, nothing to do
        if (noChangesDetected) {
            this.props.parentSetData({success: 'No data to update'});
            return;
        }

        Promise.all([
            request(arc.path.api, {
                service: 'arcimedes',
                action: 'open.datasource.table.Data.updateByTableCode',
                params: [UNDERWRITING_SINGLE_TENANT, {
                    value: [underwritingData],
                }]
            }),
            request(arc.path.api, {
                service: 'arcimedes',
                action: 'open.datasource.table.Data.updateByTableCode',
                params: [UNDERWRITING_RENT_SCHEDULE, {
                    value: newRentScheduleData,
                }]
            }),
        ]).then(res => {
            if (!res || !res[0] || (!res[0].success && Object.keys(underwritingData).length) ||
                !res[1] || (!res[1].success && newRentScheduleData.length)) {
                this.props.parentSetData({error: 'Unidentified Error'});
                return;
            }

            this.getUnderwritingData(true);
        });
    }

    render() {
        const data = this.state.data;
        const tables = data.tables;
        const agents = this.props.agents;
        const propertyImage = this.props.data.PHOTOS ? this.props.data.PHOTOS.split(',')[0] : '';
        const disabled = !(this.props.isSuper || this.props.agentIsCurrentUser && ['Proposal', 'Property Info', 'Off Market', 'Removed'].includes(this.props.data.STATUS));

        return (
            <div>
                <PrintSingleTenant location={this.props.location} tables={tables} propertyImage={propertyImage} agents={agents}/>
                <div className='Financials_content single_tenant_content'>
                    <div onMouseEnter={this.togglePopupMessage} onMouseLeave={this.togglePopupMessage} className='placeholder-button'/>
                    <button className={`Financials_submit_button${disabled ? '_disabled' : ''}`} onClick={this.onSubmit} disabled={disabled}>Submit</button>
                    {disabled && this.state.displayPopupSubmit ? <div className='Financials_submit_message'>Don't have permission to save.</div> : null}
                    <div className='Financials_general_information_table'>
                        {UnderwritingSingleTenant.renderTableContainer(tables, 'general-information', [this.renderTable(tables, 'general-information')])}
                    </div>
                    <div className='Financials_list_columns'>
                        <div className='Financials_list_column'>
                            {UnderwritingSingleTenant.renderTableContainer(tables, 'offering-summary', [this.renderTable(tables, 'offering-summary')])}

                            {UnderwritingSingleTenant.renderTableContainer(tables, 'lease-summary', [this.renderTable(tables, 'lease-summary')])}

                            {UnderwritingSingleTenant.renderTableContainer(tables, 'annualized-operating-data', [this.renderMatrixTable('annualized-operating-data')])}

                            {UnderwritingSingleTenant.renderTableContainer(tables, 'lease-notes', [
                                <Textarea
                                    type='text'
                                    key=""
                                    name='LEASE NOTES'
                                    disabled={this.props.isAgent}
                                    value={tables['lease-notes'].rows['lease-notes'].value}
                                    onChange={(event) => this.onTableValueChange('lease-notes', 'lease-notes', event)}
                                    label='Lease notes'/>
                            ])}
                        </div>
                        <div className='Financials_list_column'>
                            {UnderwritingSingleTenant.renderTableContainer(tables, 'pricing-matrix', [this.renderMatrixTable('pricing-matrix'),])}

                            {UnderwritingSingleTenant.renderTableContainer(tables, 'pricing-matrix-annex', [this.renderMatrixTable('pricing-matrix-annex'),])}

                            {UnderwritingSingleTenant.renderTableContainer(tables, 'rent-schedule', [this.renderMatrixTable('rent-schedule')])}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}